/**
 * Engine.ts - WebGL Context & Render Loop Manager
 * Handles canvas creation, high-DPI scaling, and animation lifecycle
 */

import { StateManager } from './StateManager';
import type { AtmosphereConfig } from './StateManager';
import { BaseEffect } from '../effects/BaseEffect';
import { createCanvas, removeElement } from '../utils/DomHelpers';

export interface EngineOptions {
  container?: HTMLElement;
  config?: Partial<AtmosphereConfig>;
}

export class Engine {
  private canvas: HTMLCanvasElement | null = null;
  private gl: WebGLRenderingContext | null = null;
  private accumulationCanvas: HTMLCanvasElement | null = null;
  private accumulationCtx: CanvasRenderingContext2D | null = null;
  
  private stateManager: StateManager;
  private effects: Map<string, BaseEffect> = new Map();
  private animationFrameId: number | null = null;
  private isRunning: boolean = false;
  private lastTime: number = 0;
  
  private container: HTMLElement;
  private resizeObserver: ResizeObserver | null = null;
  private dpr: number = 1;

  constructor(options: EngineOptions = {}) {
    this.container = options.container || document.body;
    this.stateManager = new StateManager(options.config);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
  }

  /**
   * Initialize WebGL context and canvases
   */
  private initializeCanvases(): void {
    const { width, height } = this.getContainerDimensions();

    // Main WebGL canvas for particles
    this.canvas = createCanvas('atmosphere-main-canvas', {
      width: width * this.dpr,
      height: height * this.dpr,
      style: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: '999997',
      },
    });

    // Get WebGL context
    this.gl = this.canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
      preserveDrawingBuffer: false,
    });

    if (!this.gl) {
      throw new Error('WebGL not supported');
    }

    // Accumulation canvas (Canvas 2D for smooth ground snow)
    this.accumulationCanvas = createCanvas('atmosphere-accumulation-canvas', {
      width: width,
      height: height,
      style: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: '999996',
      },
    });

    this.accumulationCtx = this.accumulationCanvas.getContext('2d');

    // Append to container
    this.container.appendChild(this.accumulationCanvas);
    this.container.appendChild(this.canvas);

    // Setup resize observer
    this.setupResizeObserver();
  }

  /**
   * Get container dimensions
   */
  private getContainerDimensions(): { width: number; height: number } {
    if (this.container === document.body) {
      return {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }
    const rect = this.container.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }

  /**
   * Setup resize observer for responsive canvas
   */
  private setupResizeObserver(): void {
    const handleResize = () => {
      const { width, height } = this.getContainerDimensions();

      if (this.canvas) {
        this.canvas.width = width * this.dpr;
        this.canvas.height = height * this.dpr;
      }

      if (this.accumulationCanvas) {
        this.accumulationCanvas.width = width;
        this.accumulationCanvas.height = height;
      }

      if (this.gl) {
        this.gl.viewport(0, 0, width * this.dpr, height * this.dpr);
      }

      // Notify effects of resize
      for (const effect of this.effects.values()) {
        effect.onResize(width, height, this.dpr);
      }
    };

    this.resizeObserver = new ResizeObserver(handleResize);
    
    if (this.container === document.body) {
      window.addEventListener('resize', handleResize);
    } else {
      this.resizeObserver.observe(this.container);
    }

    // Initial resize
    handleResize();
  }

  /**
   * Register an effect
   */
  registerEffect(name: string, effect: BaseEffect): void {
    this.effects.set(name, effect);
    
    if (this.gl && this.accumulationCtx) {
      const { width, height } = this.getContainerDimensions();
      effect.initialize(this.gl, this.accumulationCtx, this.stateManager.getConfig());
      effect.onResize(width, height, this.dpr);
    }
  }

  /**
   * Remove an effect
   */
  removeEffect(name: string): void {
    const effect = this.effects.get(name);
    if (effect) {
      effect.dispose();
      this.effects.delete(name);
    }
  }

  /**
   * Main render loop
   */
  private render = (currentTime: number): void => {
    if (!this.isRunning) return;

    const deltaTime = this.lastTime ? (currentTime - this.lastTime) / 1000 : 0.016;
    this.lastTime = currentTime;

    const config = this.stateManager.getConfig();

    if (!config.enabled) {
      this.animationFrameId = requestAnimationFrame(this.render);
      return;
    }

    // Clear WebGL canvas
    if (this.gl) {
      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }

    // Update and render all effects
    for (const effect of this.effects.values()) {
      effect.update(deltaTime, currentTime);
      effect.render();
    }

    this.animationFrameId = requestAnimationFrame(this.render);
  };

  /**
   * Start the engine
   */
  start(): void {
    if (this.isRunning) return;

    this.initializeCanvases();
    
    // Initialize all registered effects
    for (const effect of this.effects.values()) {
      if (this.gl && this.accumulationCtx) {
        const { width, height } = this.getContainerDimensions();
        effect.initialize(this.gl, this.accumulationCtx, this.stateManager.getConfig());
        effect.onResize(width, height, this.dpr);
      }
    }

    this.isRunning = true;
    this.lastTime = 0;
    this.animationFrameId = requestAnimationFrame(this.render);
  }

  /**
   * Stop the engine
   */
  stop(): void {
    this.isRunning = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Dispose all effects
    for (const effect of this.effects.values()) {
      effect.dispose();
    }

    // Cleanup resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Remove canvases
    if (this.canvas) {
      removeElement(this.canvas);
      this.canvas = null;
    }

    if (this.accumulationCanvas) {
      removeElement(this.accumulationCanvas);
      this.accumulationCanvas = null;
    }

    this.gl = null;
    this.accumulationCtx = null;
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<AtmosphereConfig>): void {
    this.stateManager.updateConfig(updates);
    
    const newConfig = this.stateManager.getConfig();
    for (const effect of this.effects.values()) {
      effect.onConfigChange(newConfig);
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): Readonly<AtmosphereConfig> {
    return this.stateManager.getConfig();
  }

  /**
   * Get WebGL context
   */
  getGL(): WebGLRenderingContext | null {
    return this.gl;
  }

  /**
   * Get accumulation canvas context
   */
  getAccumulationCtx(): CanvasRenderingContext2D | null {
    return this.accumulationCtx;
  }

  /**
   * Check if engine is running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }
}

